import { Badge, Box, Button, FormControl, IconButton, makeStyles, Popover, SvgIcon, TextField, Tooltip, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { capitalCase } from 'change-case';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Settings as SettingsIcon } from 'react-feather';
import Truncate from 'src/components/Truncate';
import { THEMES } from 'src/constants';
import useGetClients from 'src/hooks/useGetClients';
import useSettings from 'src/hooks/useSettings';
import type { Theme } from 'src/theme';
import { getSelectedClientId, getSelectedClientName } from 'src/utils/client';
import { logToCloud } from 'src/utils/common';

interface Client {
  id: number;
  name: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  badge: {
    height: 10,
    width: 10,
    borderRadius: 5,
    marginTop: 10,
    marginRight: 5
  },
  popover: {
    width: 320,
    padding: theme.spacing(2)
  },
  formControl: {
    minWidth: 120,
  },
}));

const Settings: FC = () => {
  const classes = useStyles();
  const ref = useRef<any>(null);
  const { settings, saveSettings } = useSettings();
  const [isOpen, setOpen] = useState<boolean>(false);
  const clients = useGetClients();
  const [values, setValues] = useState({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
    client: getSelectedClientId(),
    clientName: getSelectedClientName()
  });
  const prevSettingsClient = useRef(settings.client);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleChange = (field, value): void => {
    setValues({
      ...values,
      [field]: value
    });
  };

  const handleSave = (): void => {
    saveSettings(values);
    setOpen(false);
  };

  useEffect(() => {
    if (settings.client !== prevSettingsClient.current) {
      prevSettingsClient.current = settings.client;
      const { client, clientName } = settings;
      logToCloud({ message: 'Client has been changed', data: { client, clientName } });
      window.location.reload();
    }
  }, [settings]);

  return (
    <>
      <Tooltip title="Settings">
        <Badge
          color="secondary"
          variant="dot"
          classes={{ badge: classes.badge }}
        >
          <IconButton
            color="inherit"
            onClick={handleOpen}
            ref={ref}
          >
            <SvgIcon fontSize="small">
              <SettingsIcon />
            </SvgIcon>
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Typography
          variant="h4"
          color="textPrimary"
        >
          Settings
        </Typography>
        {/* <Box
          mt={2}
          px={1}
        >
          <FormControlLabel
            control={(
              <Switch
                checked={values.direction === 'rtl'}
                edge="start"
                name="direction"
                onChange={(event) => handleChange('direction', event.target.checked ? 'rtl' : 'ltr')}
              />
            )}
            label="RTL"
          />
        </Box>
        <Box
          mt={2}
          px={1}
        >
          <FormControlLabel
            control={(
              <Switch
                checked={values.responsiveFontSizes}
                edge="start"
                name="direction"
                onChange={(event) => handleChange('responsiveFontSizes', event.target.checked)}
              />
            )}
            label="Responsive font sizes"
          />
        </Box> */}
        <Box mt={2}>
          <TextField
            fullWidth
            label="Theme"
            name="theme"
            onChange={(event) => handleChange('theme', event.target.value)}
            select
            SelectProps={{ native: true }}
            value={values.theme}
            variant="outlined"
          >
            {Object.keys(THEMES).map((theme) => (
              <option
                key={theme}
                value={theme}
              >
                {capitalCase(theme)}
              </option>
            ))}
          </TextField>
        </Box>
        <Box mt={2}>
          <FormControl fullWidth variant="outlined" className={classes.formControl}>
            <Autocomplete
              id="client"
              value={clients.find(item => item.id === values.client)}
              options={clients as Client[]}
              getOptionLabel={(option: Client) => option.name}
              onChange={(e, value: Client) => {
                setValues({
                  ...values,
                  client: value?.id || null,
                  clientName: value?.name || ''
                });
              }}
              autoHighlight
              disableClearable
              renderOption={({ name, ...props }) => {
                return (
                  <Truncate value={name} length={50} />
                );
              }}
              renderInput={(params) => {
                return <TextField
                  {...params}
                  fullWidth
                  label="Client"
                  name="client"
                  variant="outlined" />;
              }}
            />
          </FormControl>
        </Box>
        <Box mt={2}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleSave}
          >
            Save Settings
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default Settings;
